import {create} from 'zustand';

import type {TextFormat, TextMarkStyle} from './rich-text';

/**
 * The text editor that is open on the canvas, and what is selected in it.
 *
 * The toolbar and the editor are far apart in the tree — the toolbar sits above
 * the page, the editor inside an element inside a table cell — and the toolbar
 * needs exactly two things from it: how the selected words read, and a way to
 * format them. This is that handshake and nothing more; the text itself stays
 * the editor's.
 *
 * `owner` is the id of the ELEMENT being edited: a header's own id, or the
 * table's for a cell. A toolbar formats the selection only when it is showing
 * that same element, so a stale session can never format the wrong thing.
 */
export interface RichTextSession {
  owner: string;
  /** How the selection reads — see `selectionFormat`. */
  format: TextFormat;
  apply: (patch: TextMarkStyle) => void;
}

interface RichTextSessionState {
  session: RichTextSession | null;
  setSession: (session: RichTextSession | null) => void;
  /** Close `owner`'s session, unless another editor has already opened its own. */
  clearSession: (owner: string, apply: RichTextSession['apply']) => void;
}

export const useRichTextSession = create<RichTextSessionState>((set) => ({
  session: null,
  setSession: (session) => set({session}),
  clearSession: (owner, apply) =>
    set((s) => (s.session?.owner === owner && s.session.apply === apply ? {session: null} : s)),
}));

/**
 * What the toolbar's text controls act on (DERP-307): the words selected in
 * `owner`'s open editor while there are some, and the whole element otherwise.
 * `whole` is how the element reads, and `patchWhole` writes a change to all of it.
 */
export function useTextFormat(
  owner: string,
  whole: TextFormat,
  patchWhole: (patch: TextMarkStyle) => void,
) {
  const selection = useRichTextSession((s) => (s.session?.owner === owner ? s.session : null));
  return {
    format: selection?.format ?? whole,
    /** Whether the controls are formatting selected words, not the element. */
    selecting: selection !== null,
    set: (patch: TextMarkStyle) => (selection ? selection.apply(patch) : patchWhole(patch)),
  };
}

/**
 * Marks a control that formats the selection: focus moving into it does not end
 * the edit. Set on the toolbar, so pressing Bold or typing a font size keeps the
 * selected words selected rather than committing and closing the editor.
 */
export const KEEPS_TEXT_SELECTION = 'data-keeps-text-selection';
